/**
 * Challenge — the findings worth arguing with before you re-publish.
 *
 * Reads whatever the intelligence agents already persisted (Fracture, Compass,
 * Keystone). Accepting or dismissing is a real governance act and goes through
 * the canonical findings store; this app never runs a detector itself, so
 * nothing here can silently rewrite what those agents concluded.
 */

import { useEffect, useState } from 'react';
import { AlertTriangle, Check, X } from 'lucide-react';
import { useAgentFindingsStore } from '@/stores/prioritization/agentFindingsStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import type { AgentFinding } from '@/lib/agentFindingsService';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import { AppCard, EmptyState, Eyebrow, SecondaryButton, StatusChip } from '../components/primitives';

/** The three detectors whose findings bear on "is this ranking still true?" */
const AGENTS: Array<{ type: string; label: string }> = [
  { type: 'cassandra', label: 'Fracture — fragile rankings' },
  { type: 'compass', label: 'Compass — strategy drift' },
  { type: 'keystone', label: 'Keystone — load-bearing work' },
];

const SEVERITY_TONE: Record<string, 'coral' | 'mustard' | 'neutral'> = {
  high: 'coral',
  critical: 'coral',
  medium: 'mustard',
  low: 'neutral',
};

export default function ChallengeScreen() {
  const app = getAppDefinition('review')!;
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const userId = useUserStore((s) => s.userId);
  const byAgent = useAgentFindingsStore((s) => s.byAgent);
  const fetchActive = useAgentFindingsStore((s) => s.fetchActive);
  const accept = useAgentFindingsStore((s) => s.accept);
  const dismiss = useAgentFindingsStore((s) => s.dismiss);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actError, setActError] = useState<string | null>(null);

  useEffect(() => {
    if (!workspaceId) return;
    for (const a of AGENTS) void fetchActive(workspaceId, a.type);
  }, [workspaceId, fetchActive]);

  const act = async (agentType: string, findingId: string, action: 'accept' | 'dismiss') => {
    if (!userId) return;
    setBusyId(findingId);
    setActError(null);
    const result =
      action === 'accept'
        ? await accept(agentType, findingId, userId)
        : await dismiss(agentType, findingId, userId);
    setBusyId(null);
    if (!result.success) {
      // The finding stays active in the store on failure — just say why.
      setActError(result.error ?? `Could not ${action} that finding. Try again.`);
    }
  };

  const total = AGENTS.reduce((n, a) => n + (byAgent?.[a.type]?.active?.length ?? 0), 0);

  return (
    <AppShell app={app} backTo="/apps/review" backLabel="Back to Then vs Now">
      <ScreenHeading
        title="What's worth challenging"
        subtitle="What the detectors flagged about this plan — accept it or argue with it before you re-publish."
      />
      <BallLine ballWith="you — a change without a named reason is a whim" />

      {actError && (
        <p className="font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">
          {actError}
        </p>
      )}

      {total === 0 ? (
        <EmptyState
          title="Nothing outstanding"
          hint="Findings show up here when Fracture, Compass, or Keystone run and surface something."
        />
      ) : (
        <div className="space-y-4">
          {AGENTS.map((agent) => {
            const findings: AgentFinding[] = byAgent?.[agent.type]?.active ?? [];
            if (findings.length === 0) return null;
            return (
              <AppCard key={agent.type} className="!p-0">
                <div className="flex items-center gap-2 px-5 pt-5">
                  <AlertTriangle className="h-4 w-4 text-electric-mustard-600" />
                  <Eyebrow>
                    {agent.label} · {findings.length}
                  </Eyebrow>
                </div>
                <ul className="mt-2">
                  {findings.map((f) => (
                    <li
                      key={f.id}
                      className="border-b border-gray-100 px-5 py-3 last:border-0 dark:border-[var(--rule)]"
                    >
                      <div className="flex items-start gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="font-body text-sm font-medium text-gray-900 dark:text-[var(--text-pri)]">
                            {f.title}
                          </p>
                          <p className="mt-0.5 font-body text-xs text-gray-600 dark:text-[var(--text-sec)]">
                            {f.summary}
                          </p>
                        </div>
                        <StatusChip
                          label={f.severity}
                          tone={SEVERITY_TONE[f.severity] ?? 'neutral'}
                        />
                      </div>
                      <div className="mt-2 flex gap-2">
                        <SecondaryButton
                          onClick={() => void act(agent.type, f.id, 'accept')}
                          disabled={busyId === f.id}
                          className="!px-2.5 !py-1 !text-xs"
                        >
                          <Check className="h-3 w-3" /> Accept
                        </SecondaryButton>
                        <SecondaryButton
                          onClick={() => void act(agent.type, f.id, 'dismiss')}
                          disabled={busyId === f.id}
                          className="!px-2.5 !py-1 !text-xs"
                        >
                          <X className="h-3 w-3" /> Dismiss
                        </SecondaryButton>
                      </div>
                    </li>
                  ))}
                </ul>
              </AppCard>
            );
          })}
        </div>
      )}
    </AppShell>
  );
}
